import { useState, useMemo } from "react";
import {
  MessageSquare,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Reply,
  Eye,
  RotateCcw,
  X,
  Check,
  Clock,
  Send,
  Paperclip,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import { queryClient } from "@/services/queryClient";
import {
  SupportPriority,
  SupportStatus,
  type SupportTicket,
} from "@/interfaces/ticket";
import Loader from "@/components/loader/Loader";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { UserRole } from "@/interfaces/profile";

const AdminSupport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null,
  );
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");

  const itemsPerPage = 10;
  const navigate = useNavigate();

  // const mapBackendTicket = (t: any): SupportTicket => ({
  //   id: t.id,
  //   user:
  //     `${t.user?.firstName || ""} ${t.user?.lastName || ""}`.trim() ||
  //     "Unknown",
  //   email: t.user?.email || "",
  //   subject: t.subject,
  //   category: t.category,
  //   status: t.status as SupportStatus,
  //   priority: t.priority as SupportPriority,
  //   createdAt: new Date(t.createdAt)
  //     .toISOString()
  //     .slice(0, 16)
  //     .replace("T", " "),
  //   lastReply: t.updatedAt
  //     ? new Date(t.updatedAt).toISOString().slice(0, 16).replace("T", " ")
  //     : new Date(t.createdAt).toISOString().slice(0, 16).replace("T", " "),
  //   messages: (t.replies || []).map((r: any) => ({
  //     sender: r.user
  //       ? `${r.user.firstName} ${r.user.lastName}`.trim()
  //       : "Support Team",
  //     content: r.text,
  //     time: new Date(r.createdAt).toISOString().slice(0, 16).replace("T", " "),
  //   })),
  // });

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ["tickets", { status: statusFilter, priority: priorityFilter }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: "1",
        limit: "100",
        ...(statusFilter !== "all" && { status: statusFilter.toUpperCase() }),
        ...(priorityFilter !== "all" && {
          priority: priorityFilter.toUpperCase(),
        }),
      });

      const res = await makeRequest.get(`/ticket?${params}`);
      return res.data;
    },
    staleTime: 1000 * 60 * 3,
  });

  const tickets: SupportTicket[] = useMemo(() => {
    return ticketsData?.data || [];
  }, [ticketsData]);

  console.log("TICKETS FROM ADMIN", ticketsData);

  const replyMutation = useMutation({
    mutationFn: async ({
      ticketId,
      message,
    }: {
      ticketId: string;
      message: string;
    }) => {
      const res = await makeRequest.post(`/ticket/${ticketId}/reply`, {
        ticketId,
        message,
        images: [],
      });

      console.log(res.data);
      return res.data;
    },
    onMutate: async ({ ticketId, message }) => {
      await queryClient.cancelQueries({ queryKey: ["tickets"] });
      const previousData = queryClient.getQueryData(["tickets"]);

      queryClient.setQueryData(["tickets"], (old: any) => ({
        ...old,
        data: old?.data?.map((t: any) =>
          t.id === ticketId
            ? {
                ...t,
                status: SupportStatus.IN_PROGRESS,
                updatedAt: new Date().toISOString(),
                replies: [
                  ...(t.replies || []),
                  {
                    text: message,
                    createdAt: new Date().toISOString(),
                    user: { firstName: "Support", lastName: "Team" },
                  },
                ],
              }
            : t,
        ),
      }));

      return { previousData };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(["tickets"], context?.previousData);
      toast.error(getErrorMessage(err) || "Failed to send reply");
      console.log(err);
    },
    onSuccess: () => {
      toast.success("Reply Sent Successfully");
      setShowReplyModal(false);
      setReplyMessage("");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      ticketId,
      status,
    }: {
      ticketId: string;
      status: string;
    }) => {
      const res = await makeRequest.put(`/ticket/${ticketId}/status`, {
        status,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Ticket Updated");
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      const res = await makeRequest.put(`/ticket/${ticketId}/reopen`, {});
      return res.data;
    },
    onSuccess: () => {
      toast.success("Ticket Reopened");
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });

  const handleView = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const handleReply = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setShowReplyModal(true);
  };

  console.log(selectedTicket);

  const submitReply = () => {
    if (!selectedTicket || !replyMessage.trim()) return;
    replyMutation.mutate({
      ticketId: selectedTicket.id,
      message: replyMessage,
    });
  };

  const handleResolve = (ticket: SupportTicket) => {
    statusMutation.mutate({ ticketId: ticket.id, status: "RESOLVED" });
    setShowViewModal(false);
  };

  const handleReopen = (ticket: SupportTicket) => {
    reopenMutation.mutate(ticket.id);
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const matchesSearch =
        ticket.user.firstName
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        ticket.user.lastName
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        ticket.user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || ticket.status === statusFilter;
      const matchesPriority =
        priorityFilter === "all" || ticket.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchQuery, statusFilter, priorityFilter]);

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const openCount = tickets.filter(
    (t) => t.status === SupportStatus.OPEN,
  ).length;
  const inProgressCount = tickets.filter(
    (t) => t.status === SupportStatus.IN_PROGRESS,
  ).length;
  const resolvedCount = tickets.filter(
    (t) => t.status === SupportStatus.RESOLVED,
  ).length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case SupportStatus.OPEN:
        return (
          <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary flex items-center gap-1">
            <Clock className="w-3 h-3" /> Open
          </span>
        );
      case SupportStatus.IN_PROGRESS:
        return (
          <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-500 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> In Progress
          </span>
        );
      case SupportStatus.RESOLVED:
        return (
          <span className="px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500 flex items-center gap-1">
            <Check className="w-3 h-3" /> Resolved
          </span>
        );
      case SupportStatus.CLOSED:
        return (
          <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground flex items-center gap-1">
            <X className="w-3 h-3" /> Closed
          </span>
        );
      default:
        return <span className="text-muted-foreground">{status}</span>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case SupportPriority.URGENT:
        return (
          <span className="px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            Urgent
          </span>
        );
      case SupportPriority.HIGH:
        return (
          <span className="px-2 py-1 rounded text-xs bg-amber-500/10 text-amber-500">
            High
          </span>
        );
      case SupportPriority.MEDIUM:
        return (
          <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-500">
            Medium
          </span>
        );
      case SupportPriority.LOW:
        return (
          <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
            Low
          </span>
        );
      default:
        return <span className="text-muted-foreground">{priority}</span>;
    }
  };

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Support Tickets
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage and respond to user support requests
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {tickets.length}
                </p>
                <p className="text-xs text-muted-foreground">Total Tickets</p>
              </div>
            </div>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {openCount}
                </p>
                <p className="text-xs text-muted-foreground">Open</p>
              </div>
            </div>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {inProgressCount}
                </p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </div>
          <div className="nex-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {resolvedCount}
                </p>
                <p className="text-xs text-muted-foreground">Resolved</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tickets..."
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Status</option>
              <option value={SupportStatus.OPEN}>Open</option>
              <option value={SupportStatus.IN_PROGRESS}>In Progress</option>
              <option value={SupportStatus.RESOLVED}>Resolved</option>
              <option value={SupportStatus.CLOSED}>Closed</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="all">All Priority</option>
              <option value={SupportPriority.URGENT}>Urgent</option>
              <option value={SupportPriority.HIGH}>High</option>
              <option value={SupportPriority.MEDIUM}>Medium</option>
              <option value={SupportPriority.LOW}>Low</option>
            </select>
          </div>
        </div>

        {/* Tickets Table */}
        <div className="nex-card overflow-hidden">
          <div className="overflow-x-auto -mx-4 sm:-mx-0">
            <table className="w-full min-w-[900px] sm:min-w-0">
              <thead className="bg-card">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Ticket ID
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    User
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Subject
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Category
                  </th>
                  <th className="text-center text-xs font-medium text-muted-foreground py-3 px-4">
                    Priority
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Last Reply
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-muted-foreground"
                    >
                      Loading tickets...
                    </td>
                  </tr>
                ) : (
                  paginatedTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="border-b border-border hover:bg-muted/50 dark:hover:bg-[#252A32] transition-colors"
                    >
                      <td className="py-4 px-4">
                        <span className="text-primary font-mono text-sm">
                          {ticket.id.slice(0, 5)}...
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-foreground font-medium text-sm">
                            {ticket?.user?.firstName +
                              " " +
                              ticket?.user?.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ticket?.user?.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-foreground text-sm">
                        {ticket?.subject}
                      </td>
                      <td className="text-left py-4 px-4">
                        <span className="px-2 py-1 rounded bg-muted text-muted-foreground text-xs">
                          {ticket?.category}
                        </span>
                      </td>
                      <td className="text-center py-4 px-4">
                        {getPriorityBadge(ticket.priority)}
                      </td>
                      <td className="text-center py-4 px-4">
                        {getStatusBadge(ticket.status)}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground text-sm">
                        {ticket.lastReply}
                      </td>
                      <td className="text-left py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleView(ticket)}
                            className="p-2 text-muted-foreground hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(ticket.status === SupportStatus.OPEN ||
                            ticket.status === SupportStatus.IN_PROGRESS) && (
                            <button
                              onClick={() => handleReply(ticket)}
                              className="p-2 text-muted-foreground hover:text-emerald-500 transition-colors"
                              title="Reply"
                            >
                              <Reply className="w-4 h-4" />
                            </button>
                          )}
                          {(ticket.status === SupportStatus.RESOLVED ||
                            ticket.status === SupportStatus.CLOSED) && (
                            <button
                              onClick={() => handleReopen(ticket)}
                              className="p-2 text-muted-foreground hover:text-amber-500 transition-colors"
                              title="Reopen"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredTickets.length)} of{" "}
              {filteredTickets.length} tickets
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from(
                { length: Math.min(5, totalPages) },
                (_, i) => i + 1,
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    currentPage === page
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-card text-muted-foreground disabled:opacity-50 hover:text-foreground"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  {selectedTicket.subject}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedTicket.id}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">User</span>
                <span className="text-foreground">
                  {selectedTicket.user.firstName +
                    " " +
                    selectedTicket?.user?.lastName}{" "}
                  ({selectedTicket?.user?.email})
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Category</span>
                <span className="px-2 py-1 rounded bg-muted text-muted-foreground text-xs">
                  {selectedTicket.category}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Priority</span>
                {getPriorityBadge(selectedTicket.priority)}
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Status</span>
                {getStatusBadge(selectedTicket.status)}
              </div>
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground">
                  {new Date(selectedTicket.createdAt).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <h4 className="font-medium text-foreground">Conversation</h4>
              {selectedTicket.replies.map((msg, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg ${msg?.user?.role !== UserRole.USER ? "bg-primary/10" : "bg-muted"}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">
                      {msg?.user?.firstName + " " + msg?.user?.lastName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {msg?.createdAt}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{msg?.text}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3 mb-5">
              {(selectedTicket.status === SupportStatus.OPEN ||
                selectedTicket.status === SupportStatus.IN_PROGRESS) && (
                <>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleReply(selectedTicket);
                    }}
                    className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:brightness-110 flex items-center justify-center gap-2"
                  >
                    <Reply className="w-4 h-4" /> Reply
                  </button>
                  <button
                    onClick={() => handleResolve(selectedTicket)}
                    className="flex-1 py-2 bg-emerald-500 text-black rounded-lg font-medium hover:brightness-110"
                  >
                    Resolve
                  </button>
                </>
              )}
              {(selectedTicket.status === SupportStatus.RESOLVED ||
                selectedTicket.status === SupportStatus.CLOSED) && (
                <button
                  onClick={() => handleReopen(selectedTicket)}
                  className="w-full py-2 bg-amber-500 text-black rounded-lg font-medium hover:brightness-110 flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" /> Reopen Ticket
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  Reply to Ticket
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedTicket.id} - {selectedTicket.subject}
                </p>
              </div>
              <button
                onClick={() => setShowReplyModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Last message from{" "}
                {
                  selectedTicket.replies[selectedTicket.replies.length - 1]
                    ?.user?.firstName
                }
                :
              </p>
              <p className="text-sm text-foreground mt-1">
                {
                  selectedTicket.replies[selectedTicket.replies.length - 1]
                    ?.text
                }
              </p>
            </div>

            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your reply..."
              className="w-full h-32 p-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none"
            />

            <div className="mt-4 flex items-center gap-2 text-muted-foreground">
              <Paperclip className="w-4 h-4" />
              <span className="text-sm">Attach files (optional)</span>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setShowReplyModal(false)}
                className="flex-1 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-all"
              >
                Cancel
              </button>
              <button
                onClick={submitReply}
                disabled={!replyMessage.trim() || replyMutation.isPending}
                className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {replyMutation.isPending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupport;
